import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Typography,
  Chip,
  Alert,
  useTheme,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MicNoneIcon from "@mui/icons-material/MicNone";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface ChatComposerProps {
  isWindowOpen: boolean;
  windowExpiresAt: string | null;
  onSendMessage: (text: string) => Promise<void>;
  onOpenTemplateModal: () => void;
  sending: boolean;
}

export const ChatComposer: React.FC<ChatComposerProps> = ({
  isWindowOpen,
  windowExpiresAt,
  onSendMessage,
  onOpenTemplateModal,
  sending,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [inputText, setInputText] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const [timeRemaining, setTimeRemaining] = useState<string | null>(null);

  // Compute countdown until 24-hour reply window closes
  useEffect(() => {
    if (!isWindowOpen || !windowExpiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimer = () => {
      const diff = new Date(windowExpiresAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) {
        setTimeRemaining(`${hours}h ${mins}m`);
      } else {
        setTimeRemaining(`${mins}m`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [isWindowOpen, windowExpiresAt]);

  const handleSend = async () => {
    if (!inputText.trim() || sending || !isWindowOpen) return;
    const textToSend = inputText.trim();
    setInputText("");
    await onSendMessage(textToSend);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "transparent",
      }}
    >
      {/* 24-Hour Closed Window Alert Banner with Template CTA */}
      {!isWindowOpen && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            px: 2,
            borderRadius: "14px",
            backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "#FEF3C7",
            border: "1px solid",
            borderColor: isDark ? "rgba(245, 158, 11, 0.28)" : "#FDE68A",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ minWidth: 0 }}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                color: isDark ? "#FBBF24" : "#D97706",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.82rem",
                color: isDark ? "#FDE68A" : "#92400E",
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              24-Hour customer window is closed. Meta WhatsApp requires an approved template to contact or re-engage this customer.
            </Typography>
          </Stack>

          <Button
            variant="contained"
            size="small"
            onClick={onOpenTemplateModal}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              flexShrink: 0,
              borderRadius: "10px",
              backgroundColor: "#724E41",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: "0.8rem",
              textTransform: "none",
              px: 2.2,
              py: 0.7,
              boxShadow: "0 2px 8px rgba(114, 78, 65, 0.25)",
              "&:hover": { backgroundColor: "#5D3E33" },
            }}
          >
            Send Template
          </Button>
        </Box>
      )}

      {/* Active Window Countdown Indicator */}
      {isWindowOpen && timeRemaining && (
        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1, px: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#22C55E",
              boxShadow: "0 0 6px rgba(34, 197, 94, 0.8)",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: isDark ? "#86EFAC" : "#166534",
              fontWeight: 600,
              fontSize: "0.74rem",
            }}
          >
            24h Window Active • Freeform replies allowed for next {timeRemaining}
          </Typography>
        </Stack>
      )}

      {/* Pill-shaped modern composer bar with transparent background */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 0.6,
          pl: 2,
          borderRadius: "28px",
          backgroundColor: isDark
            ? isWindowOpen ? "transparent" : "rgba(255, 255, 255, 0.02)"
            : isWindowOpen ? "transparent" : "#F8FAFC",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.18)" : "1px solid #CBD5E1",
          boxShadow: "none",
          transition: "all 0.2s ease",
          "&:focus-within": {
            borderColor: isWindowOpen ? "#724E41" : undefined,
            backgroundColor: "transparent",
            boxShadow: isWindowOpen ? "0 0 0 1px #724E41" : undefined,
          },
        }}
      >
        {/* Template Quick Button */}
        <Tooltip title="Send approved Meta WhatsApp template">
          <IconButton
            size="small"
            onClick={onOpenTemplateModal}
            sx={{
              color: "#724E41",
              "&:hover": { backgroundColor: "rgba(114, 78, 65, 0.08)" },
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {/* Input Text Field */}
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          variant="standard"
          disabled={!isWindowOpen || sending}
          placeholder={
            isWindowOpen
              ? "Type a message..."
              : "24h window closed — click 'Send Template' to contact customer"
          }
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            disableUnderline: true,
            sx: {
              color: isDark ? "#FFFFFF" : "#0F172A",
              fontSize: "0.95rem",
              lineHeight: 1.5,
              py: 0.5,
              "&.Mui-disabled": {
                color: isDark ? "rgba(255, 255, 255, 0.35)" : "#94A3B8",
              },
            },
          }}
        />

        {/* Mic / voice note placeholder icon */}
        <Tooltip title="Voice message feature coming soon">
          <IconButton
            size="small"
            disabled={!isWindowOpen}
            sx={{
              color: isDark ? "#94A3B8" : "#94A3B8",
              "&:hover": { color: "#724E41" },
            }}
          >
            <MicNoneIcon sx={{ fontSize: 21 }} />
          </IconButton>
        </Tooltip>

        {/* Send Button: vibrant rounded green button */}
        <Tooltip
          title={
            !isWindowOpen
              ? "Customer window is closed. Please send a template message."
              : ""
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || !inputText.trim() || !isWindowOpen}
              startIcon={
                sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
              sx={{
                borderRadius: "24px",
                px: 3.5,
                py: 0.9,
                backgroundColor: "#25D366",
                color: "#FFFFFF",
                fontWeight: 700,
                textTransform: "none",
                fontSize: "0.92rem",
                boxShadow: "0 2px 10px rgba(37, 211, 102, 0.35)",
                "&:hover": {
                  backgroundColor: "#1eaf53",
                  boxShadow: "0 4px 14px rgba(37, 211, 102, 0.45)",
                },
                "&.Mui-disabled": {
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#E2E8F0",
                  color: isDark ? "rgba(255, 255, 255, 0.3)" : "#94A3B8",
                },
              }}
            >
              Send
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

